import type { ReactNode } from 'react';
import styled from 'styled-components';
import { LinkSimpleIcon } from '@phosphor-icons/react';
import { colors, radii, spacing, typography } from '../../../theme-v2';

export interface ShareLinkRowProps {
  /** Label shown above the URL (e.g. "Anyone with this link"). Omitted entirely when not given. */
  label?: ReactNode;
  /** The URL to display, read-only. */
  url: string;
  className?: string;
  /** Label font weight. Defaults to `ShareAccessFields`' look (semibold). */
  labelFontWeight?: number;
  /** Gap between the label and the url box, in px. Defaults to `ShareAccessFields`' look (16px). */
  labelGap?: number;
}

/**
 * Read-only "label + link icon + url" row — the visual core shared by the
 * owner's `ShareAccessFields` (editable access settings) and the viewer-facing
 * `ShareLinkModal` (read-only share link). Pure display: no copy button, no
 * interaction — callers compose those around it.
 *
 * `labelFontWeight` / `labelGap` exist because the two callers want a
 * different label treatment (see "Component Composition" in .claude/CLAUDE.md
 * — a small per-caller difference goes in as a prop, not a hidden variant
 * branch) — changing the default would silently restyle the other caller.
 */
export const ShareLinkRow = ({
  label,
  url,
  className,
  labelFontWeight = typography.weight.semibold,
  labelGap = spacing.base,
}: ShareLinkRowProps) => (
  <div className={className}>
    {label && (
      <StyledLabel $fontWeight={labelFontWeight} $gap={labelGap}>
        {label}
      </StyledLabel>
    )}
    <StyledLinkRow>
      <LinkSimpleIcon size={18} weight="bold" />
      <span>{url}</span>
    </StyledLinkRow>
  </div>
);

export default ShareLinkRow;

// ─── Styled components ───────────────────────────────────────────────────

const StyledLabel = styled.label<{ $fontWeight: number; $gap: number }>`
  display: block;
  margin-bottom: ${({ $gap }) => $gap}px;
  font-size: ${typography.size.md}px;
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${colors.text.primary};
`;

const StyledLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
  padding: ${spacing.md}px;
  font-size: ${typography.size.md}px;
  color: ${colors.text.secondary};
  word-break: break-all;
  background: ${colors.surface.muted};
  border: 1px solid ${colors.border.subtle};
  border-radius: ${radii.box}px;

  svg {
    flex-shrink: 0;
    color: ${colors.accent.base};
  }
`;
